import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

export interface PinInputProps {
  /** Anzahl der Ziffern: 4 fuer die Throw-PIN, 6 fuer den TOTP-Code. */
  length: 4 | 6;
  value: string;
  onChange: (value: string) => void;
  /** Wird aufgerufen, sobald alle Ziffern gefuellt sind. */
  onComplete?: (value: string) => void;
  label: string;
  /** Zusaetzliche Beschreibung unter dem Feld, per aria-describedby verknuepft. */
  description?: string;
  /** Fehlermeldung. Setzt aria-invalid und wird per Live-Region angesagt. */
  error?: string | undefined;
  disabled?: boolean;
  autoFocus?: boolean;
}

const DIGITS = /^[0-9]+$/;

/**
 * Ziffernweise Eingabe fuer PINs und TOTP-Codes.
 *
 * Der Wert wird ausserhalb gehalten (kontrollierte Komponente), damit die
 * aufrufende Ansicht ihn validieren und zuruecksetzen kann.
 *
 * Bedienung, die ohne Erwaehnung im Design-Dokument trotzdem selbstverstaendlich
 * erwartet wird und deshalb hier implementiert ist:
 * - Einfuegen eines vollstaendigen Codes fuellt alle Felder auf einmal. Ohne das
 *   ist der Code aus einer Authenticator-App nicht per Zwischenablage nutzbar.
 * - Backspace im leeren Feld springt zurueck, statt ins Leere zu laufen.
 * - Pfeiltasten bewegen zwischen den Feldern.
 * - `inputMode="numeric"` blendet auf Mobilgeraeten die Zahlentastatur ein.
 * - `autoComplete="one-time-code"` laesst iOS und Android einen per SMS oder
 *   Systemvorschlag erkannten Code direkt anbieten.
 *
 * Erscheint ein `error`, springt der Fokus zurueck auf das erste Feld. Den Wert
 * leert die Komponente dabei bewusst NICHT selbst: sie muesste dafuer `onChange`
 * rufen, und jeder Aufrufer deutet ein `onChange` zu Recht als "der Nutzer tippt,
 * also Fehler ausblenden" -- der Fehler wuerde sich dadurch sofort selbst
 * loeschen und nie sichtbar werden. Wer den Wert nach einem Fehler leeren will,
 * setzt ihn direkt zurueck (siehe Aufrufer).
 */
export function PinInput({
  length,
  value,
  onChange,
  onComplete,
  label,
  description,
  error,
  disabled = false,
  autoFocus = false,
}: PinInputProps): React.ReactElement {
  const groupId = useId();
  const descriptionId = `${groupId}-description`;
  const errorId = `${groupId}-error`;
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  const [lastCompleted, setLastCompleted] = useState<string | null>(null);

  const digits = useMemo(() => {
    const chars = value.slice(0, length).split("");
    return Array.from({ length }, (_, i) => chars[i] ?? "");
  }, [value, length]);

  const focusField = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, inputs.current.length - 1));
    const field = inputs.current[clamped];
    field?.focus();
    field?.select();
  }, []);

  // Nach einem Fehler zurueck an den Anfang, damit der naechste Versuch ohne
  // Mausklick moeglich ist.
  useEffect(() => {
    if (!error || disabled) return;
    focusField(0);
  }, [error, disabled, focusField]);

  // onComplete genau einmal pro vollstaendigem Wert melden -- sonst wuerde ein
  // erneutes Rendern denselben Code mehrfach abschicken.
  useEffect(() => {
    if (value.length === length && DIGITS.test(value)) {
      if (lastCompleted !== value) {
        setLastCompleted(value);
        onComplete?.(value);
      }
    } else if (lastCompleted !== null) {
      setLastCompleted(null);
    }
  }, [value, length, onComplete, lastCompleted]);

  function writeDigits(startIndex: number, incoming: string): void {
    const clean = incoming.replace(/\D/g, "");
    if (clean.length === 0) return;

    const next = [...digits];
    let cursor = startIndex;
    for (const char of clean) {
      if (cursor >= length) break;
      next[cursor] = char;
      cursor += 1;
    }
    onChange(next.join("").slice(0, length));
    focusField(cursor);
  }

  function handleChange(index: number, raw: string): void {
    // Beim Ersetzen einer bestehenden Ziffer liefert der Browser beide Zeichen.
    // Die zuletzt getippte gewinnt.
    const typed = raw.replace(/\D/g, "");
    if (typed.length === 0) {
      const next = [...digits];
      next[index] = "";
      onChange(next.join(""));
      return;
    }
    writeDigits(index, typed.length > 1 ? typed.slice(-1) : typed);
  }

  function handleKeyDown(index: number, event: React.KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case "Backspace": {
        if (digits[index]) return; // Der Browser leert das Feld selbst.
        event.preventDefault();
        const next = [...digits];
        next[index - 1] = "";
        onChange(next.join("").replace(/\s/g, ""));
        focusField(index - 1);
        break;
      }
      case "ArrowLeft":
        event.preventDefault();
        focusField(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        focusField(index + 1);
        break;
      case "Home":
        event.preventDefault();
        focusField(0);
        break;
      case "End":
        event.preventDefault();
        focusField(length - 1);
        break;
      default:
        break;
    }
  }

  function handlePaste(index: number, event: React.ClipboardEvent<HTMLInputElement>): void {
    event.preventDefault();
    writeDigits(index, event.clipboardData.getData("text"));
  }

  return (
    <div>
      <div
        role="group"
        aria-labelledby={`${groupId}-label`}
        aria-describedby={
          [description ? descriptionId : null, error ? errorId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        className="pin"
      >
        <span id={`${groupId}-label`} className="visually-hidden">
          {label}
        </span>
        {digits.map((digit, index) => (
          <input
            // Die Position ist die Identitaet des Feldes; es gibt keine andere
            // stabile Kennung und die Reihenfolge aendert sich nie.
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            className="pin__field"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            aria-label={`Ziffer ${index + 1} von ${length}`}
            aria-invalid={error ? true : undefined}
            value={digit}
            disabled={disabled}
            autoFocus={autoFocus && index === 0}
            onChange={(event) => handleChange(index, event.target.value)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onFocus={(event) => event.target.select()}
          />
        ))}
      </div>

      {description ? (
        <p
          id={descriptionId}
          style={{ marginTop: "var(--space-1)", fontSize: "var(--text-small)", color: "var(--color-granite)" }}
        >
          {description}
        </p>
      ) : null}

      {/*
        Live-Region: der Fehler wird angesagt, sobald er erscheint. `assertive`,
        weil er die aktuelle Eingabe unmittelbar betrifft.
      */}
      <p
        id={errorId}
        role="alert"
        aria-live="assertive"
        style={{
          marginTop: "var(--space-1)",
          fontSize: "var(--text-small)",
          color: "var(--color-danger)",
          minHeight: error ? undefined : 0,
        }}
      >
        {error ?? ""}
      </p>
    </div>
  );
}
